import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Contact } from "@/components/site/Contact";
import { MASSAGES, PLANITY_URL } from "@/data/content";
import { useSeo } from "@/hooks/useSeo";
import { DotText } from "@/components/site/DotText";

export default function MassageDetail() {
  const { slug } = useParams();
  const m = MASSAGES.find((x) => x.slug === slug);

  useSeo({
    title: m ? `${m.name} à Toulouse — Ninaa Beauty` : "",
    description: m
      ? `${m.tagline} ${m.name} : un massage bien-être à Toulouse centre, chez Ninaa Beauty, près de Jean-Jaurès et Matabiau.`
      : "",
    path: m ? `/massages/${m.slug}` : "",
  });

  if (!m) return <Navigate to="/massages" replace />;

  return (
    <div className="App" data-testid="massage-detail-page">
      <Header />
      <main className="page-enter bg-white">
        <div className="max-w-6xl mx-auto px-6 py-14 md:py-20">
          <Link
            to="/massages"
            data-testid="back-to-massages"
            className="inline-flex items-center gap-2 text-sm text-[#4b2018] hover:text-[#4b2018] transition-colors mb-10"
          >
            <ArrowLeft size={16} /> Tous les massages
          </Link>

          {m.slug === "soin-rebozo" && m.intro ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-12 md:gap-x-16 md:gap-y-14 md:items-start">
                {/* Image — colonne gauche */}
                <div className="md:col-start-1 md:row-start-1 overflow-hidden rounded-md">
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={m.image}
                      alt={`${m.name} à Toulouse — Ninaa Beauty`}
                      data-testid="detail-image"
                      className="h-full w-full object-cover"
                      loading="eager"
                    />
                  </div>
                </div>

                {/* Présentation générale — colonne droite, sur toute la hauteur */}
                <div className="md:col-start-2 md:row-start-1 md:row-span-2">
                  <p className="text-xs tracking-[0.3em] uppercase text-[#b07a63] mb-5">
                    {m.category || "Massage"}
                  </p>
                  <h1
                    data-testid="detail-name"
                    className="font-serif-display font-light text-[#4b2018] text-4xl md:text-6xl leading-[1]"
                  >
                    {m.name}
                  </h1>
                  <p className="mt-4 font-serif-display text-2xl md:text-3xl text-[#b07a63]">
                    {m.tagline}
                  </p>

                  <div className="mt-8 space-y-6 text-[#4b2018] leading-relaxed">
                    {m.intro.map((p, i) => (
                      <p key={i} data-testid={`rebozo-intro-${i}`}>
                        {p}
                      </p>
                    ))}
                  </div>

                  {/* Conclusion, légèrement distincte du corps du texte */}
                  <p
                    data-testid="rebozo-conclusion"
                    className="mt-8 md:mt-10 font-serif-display italic text-lg md:text-xl leading-relaxed text-[#904234]"
                  >
                    {m.conclusion}
                  </p>

                  {/* Trois coches — concluent la présentation générale */}
                  <ul className="mt-8 md:mt-10 space-y-3">
                    {m.benefits.map((b) => (
                      <li
                        key={b}
                        data-testid={`benefit-${b}`}
                        className="flex items-center gap-3 text-[#4b2018]"
                      >
                        <span className="grid place-items-center h-6 w-6 rounded-full bg-[#f6f1e9] text-[#b07a63] shrink-0">
                          <Check size={14} />
                        </span>
                        {b}
                      </li>
                    ))}
                  </ul>

                  {/* Mention bien-être + bouton — colonne droite, après les coches */}
                  <p
                    data-testid="wellbeing-notice"
                    className="mt-8 text-xs text-[#a79a86]"
                  >
                    Massage de bien-être uniquement — sans visée thérapeutique ni
                    médicale.
                  </p>

                  {m.comingSoon ? (
                    <span
                      data-testid="detail-reserve-btn"
                      aria-disabled="true"
                      className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#e7ddce] text-[#a79a86] px-7 py-3.5 text-sm cursor-not-allowed select-none"
                    >
                      Bientôt disponible
                    </span>
                  ) : (
                    <a
                      href={PLANITY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="detail-reserve-btn"
                      className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#4e231b] text-white px-7 py-3.5 text-sm hover:bg-[#b07a63] transition-colors duration-400"
                    >
                      Réserver <ArrowRight size={16} />
                    </a>
                  )}
                </div>

                {/* Autour de la maternité — sous l'image, colonne gauche */}
                <div className="md:col-start-1 md:row-start-2">
                  <h2
                    data-testid="rebozo-maternity-title"
                    className="font-serif-display font-light text-[#4b2018] text-2xl md:text-3xl mb-4"
                  >
                    {m.maternityTitle}
                  </h2>
                  <p
                    data-testid="rebozo-maternity-intro"
                    className="text-[#4b2018] leading-relaxed"
                  >
                    {m.maternityIntro}
                  </p>
                  <p className="mt-5 text-[#4b2018] leading-relaxed">
                    {m.maternityClose}
                  </p>
                  <p
                    data-testid="rebozo-maternity-note"
                    className="mt-6 pl-4 border-l-2 border-[#d8c9b3] text-sm italic text-[#944639]"
                  >
                    {m.maternityNote}
                  </p>

                  {/* Encadré césarienne — reste dans la colonne gauche */}
                  <div
                    data-testid="rebozo-a-savoir"
                    className="mt-8 rounded-md border border-[#ece4d7] bg-[#faf7f2] px-5 py-4"
                  >
                    <p className="text-xs tracking-[0.2em] uppercase text-[#b07a63] mb-1.5">
                      À savoir avant votre soin
                    </p>
                    <p className="text-sm leading-relaxed text-[#4b2018]">
                      {m.aSavoir}
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-start">
              {/* grande image */}
              <div className="overflow-hidden rounded-md">
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={m.image}
                    alt={`${m.name} à Toulouse — Ninaa Beauty`}
                    data-testid="detail-image"
                    className="h-full w-full object-cover"
                    loading="eager"
                  />
                </div>
              </div>

              {/* contenu minimaliste */}
              <div>
                <p className="text-xs tracking-[0.3em] uppercase text-[#b07a63] mb-5">
                  {m.category || "Massage"}
                </p>
                <h1
                  data-testid="detail-name"
                  className="font-serif-display font-light text-[#4b2018] text-4xl md:text-6xl leading-[1]"
                >
                  {m.name}
                </h1>
                <p className="mt-4 font-serif-display text-2xl md:text-3xl text-[#b07a63]">
                  {m.tagline}
                </p>
                {m.meta && (
                  <p
                    data-testid="detail-meta"
                    className="mt-4 text-sm tracking-wide text-[#944639]"
                  >
                    <DotText text={m.meta} />
                  </p>
                )}
                {m.slug === "massage-femme-enceinte" && (
                  <span
                    data-testid="pregnancy-badge"
                    className="mt-5 inline-flex items-center rounded-full bg-[#f6f1e9] border border-[#e4dccd] px-3 py-1 text-[11px] tracking-wide text-[#944639]"
                  >
                    À partir du 2ᵉ trimestre de grossesse
                  </span>
                )}

                <p className="mt-8 text-[#4b2018] leading-relaxed max-w-md whitespace-pre-line">
                  {m.description}
                </p>
                {m.note && (
                  <p className="mt-3 text-xs italic text-[#a79a86] max-w-md">
                    {m.note}
                  </p>
                )}

                <ul className="mt-8 space-y-3">
                  {m.benefits.map((b) => (
                    <li
                      key={b}
                      data-testid={`benefit-${b}`}
                      className="flex items-center gap-3 text-[#4b2018]"
                    >
                      <span className="grid place-items-center h-6 w-6 rounded-full bg-[#f6f1e9] text-[#b07a63] shrink-0">
                        <Check size={14} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>

                {m.installNote && (
                  <p
                    data-testid="detail-install-note"
                    className="mt-8 text-xs text-[#a79a86] max-w-md"
                  >
                    La durée indiquée comprend environ 5 minutes pour vous
                    installer et vous déshabiller.
                  </p>
                )}
                <p
                  data-testid="wellbeing-notice"
                  className={`${m.installNote ? "mt-3" : "mt-8"} text-xs text-[#a79a86] max-w-md`}
                >
                  Massage de bien-être uniquement — sans visée thérapeutique ni
                  médicale.
                </p>

                {m.comingSoon ? (
                  <span
                    data-testid="detail-reserve-btn"
                    aria-disabled="true"
                    className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#e7ddce] text-[#a79a86] px-7 py-3.5 text-sm cursor-not-allowed select-none"
                  >
                    Bientôt disponible
                  </span>
                ) : (
                  <a
                    href={PLANITY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="detail-reserve-btn"
                    className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#4e231b] text-white px-7 py-3.5 text-sm hover:bg-[#b07a63] transition-colors duration-400"
                  >
                    Réserver <ArrowRight size={16} />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
      <Contact />
    </div>
  );
}
